import { httpsCallable } from 'firebase/functions';
import { addDoc, collection, doc, getDocs, query, serverTimestamp, where, writeBatch } from 'firebase/firestore';
import { auth, db, functions } from '../../../services/firebase';
import {
  MAX_CARE_SOURCE_FILE_SIZE_BYTES,
  extractTextFromCareSourceFile,
  extractTextFromPdfArrayBuffer,
} from '../../../features/careSourceProcessing/textExtraction/careSourceFileTextExtraction';
import {
  buildBehaviorImportDocData,
  buildBehaviorNoteFallbackDocData,
  buildBehaviorObservationFallbackDocData,
  buildPreservedImportDocData,
  buildParsedObservationImportDocData,
} from './importLogsDocBuilders';
import { groupRowsForReview } from '../../../utils/reviewRowGrouping';
import { normalizeCategoryText } from '../../../utils/categoryClassifier';
import { isDebugFlagEnabled } from '../../../utils/debugTools';
import { normalizeBackendCaptureReviewRows } from '../../../utils/naturalCaptureReviewNormalization';
import {
  segmentImportedCaregiverTimelineWithAI,
} from './importLogsAITimelineSegmentation';
import {
  applyImportDateContextToImportedRows,
  extractImportDateContexts,
  formatLocalTimeForStorage,
  isLongNoteEmailText,
  normalizeImportCategory,
  parseDateForStorage,
  parseNaturalCaptureText,
  resolveImportPreviewTiming,
} from './importLogsParsing';
import {
  buildNaturalCapturePipelineSnapshot,
  detectNaturalCaptureInputType,
  traceNaturalCapturePipeline,
} from './importLogsPipeline';
import {
  CAPTURE_PROCESSING_STATUSES,
  buildCaptureTimelineFields,
  createCapture,
} from '../../../features/captures';
import {
  ARTIFACT_TYPES,
  isKnowledgeItemArtifact,
  resolveTimelineArtifactType,
} from '../../../features/artifacts/artifactTypes';
import {
  KNOWLEDGE_ITEMS_COLLECTION,
  buildKnowledgeItemPayload,
} from '../../../features/knowledgeItems';
import { shouldSkipTimelineSave } from '../../../features/rawCareCaptureExtraction/sourceOnlySuggestedRecords';
import { looksLikeCaregiverTimelineImportText } from './naturalCaptureChunkingCore';
import {
  shouldUseAITimelineSegmentation,
} from './importLogsTimelineFallbackDetection';
import { countCareExplicitTimes } from '../../../shared/careDateTimePatterns';

const parseImportedLogsCallable = httpsCallable(functions, 'parseImportedLogs');

const buildImportCaptureSourceText = (rows = []) => (
  rows
    .map((row) => String(
      row?.originalText
      || row?.sourceText
      || row?.rawSourceText
      || row?.text
      || row?.body
      || row?.note
      || ''
    ).trim())
    .filter(Boolean)
    .join('\n\n')
);

const getReviewedRowArtifactType = (row = {}, docData = {}) => (
  isKnowledgeItemArtifact(row?.artifactType || docData?.artifactType)
    ? ARTIFACT_TYPES.KNOWLEDGE_ITEM
    : resolveTimelineArtifactType({
      ...row,
      category: docData.category,
      logCategory: docData.logCategory,
      metadata: docData.metadata,
      source: docData.source,
    })
);

const buildKnowledgeItemFromReviewedRow = ({
  row = {},
  docData = {},
  user = null,
} = {}) => buildKnowledgeItemPayload({
  childId: docData.childId,
  captureId: docData.captureId,
  title: row.title || docData.title,
  summary: row.summary || row.content || row.body || row.note || row.text || docData.summary || docData.text,
  knowledgeType: row.knowledgeType || row.metadata?.knowledgeType || docData.metadata?.knowledgeType,
  sourceText: row.sourceText || row.originalText || docData.sourceText || docData.originalText || docData.text,
  sourceBlockId: docData.sourceBlockId || row.sourceBlockId || row.metadata?.sourceBlockId,
  sourceSentenceIndex: Number.isInteger(docData.sourceSentenceIndex)
    ? docData.sourceSentenceIndex
    : Number.isInteger(row.sourceSentenceIndex)
      ? row.sourceSentenceIndex
      : Number.isInteger(row.metadata?.sourceSentenceIndex)
        ? row.metadata.sourceSentenceIndex
        : null,
  extractionVersion: docData.extractionVersion,
  editedByUser: Boolean(docData.editedByUser),
  capturedAt: docData.createdAt,
  capturedBy: {
    id: user?.uid || docData.createdBy || null,
    name: user?.displayName || user?.email?.split('@')[0] || docData.authorName || 'User',
    email: user?.email || docData.authorEmail || null,
  },
  createdAt: docData.createdAt,
  updatedAt: docData.updatedAt || docData.createdAt,
});

const formatLocalDateForStorage = (value) => {
  const date = value instanceof Date ? value : parseDateForStorage(value);
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';

  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-');
};

const getImportedLogsFunctionBaseUrl = () => {
  const projectId = auth?.app?.options?.projectId
    || process.env.REACT_APP_FIREBASE_PROJECT_ID
    || 'lifelog-test-aa7d6';
  return `https://us-central1-${projectId}.cloudfunctions.net`;
};

const invokeParseImportedLogsOverHttp = async (payload = {}) => {
  const currentUser = auth?.currentUser || null;
  if (!currentUser || typeof fetch !== 'function') {
    return null;
  }

  const token = await currentUser.getIdToken();
  if (!token) {
    return null;
  }

  const response = await fetch(`${getImportedLogsFunctionBaseUrl()}/httpParseImportedLogs`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ data: payload }),
  });

  let body = null;
  try {
    body = await response.json();
  } catch (_error) {
    body = null;
  }

  if (!response.ok) {
    const message = body?.error?.message || body?.message || `parseImportedLogs failed with status ${response.status}`;
    const error = new Error(message);
    error.code = body?.error?.status || response.statusText || 'internal';
    throw error;
  }

  if (body?.error) {
    const error = new Error(body.error.message || body.error || 'parseImportedLogs failed');
    error.code = body?.error?.status || 'internal';
    throw error;
  }

  return {
    data: body?.data || body?.result || {},
  };
};

const invokeParseImportedLogsCallable = async (payload = {}) => {
  const httpResponse = await invokeParseImportedLogsOverHttp(payload);
  if (httpResponse) {
    return httpResponse;
  }

  return parseImportedLogsCallable(payload);
};

const isImportParserDebugEnabled = () => {
  return isDebugFlagEnabled('debugImportParser');
};

export const MAX_IMPORT_FILE_SIZE_BYTES = MAX_CARE_SOURCE_FILE_SIZE_BYTES;
export const MAX_IMPORT_TEXT_LENGTH = 250000;
export const extractTextFromImportFile = extractTextFromCareSourceFile;
export { extractTextFromPdfArrayBuffer };


const getDateKey = (value) => {
  const date = parseDateForStorage(value);
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-');
};

const roundMinutesToNearestFive = (minutes = 0) => {
  const normalized = Number(minutes);
  if (!Number.isFinite(normalized)) return 0;
  return Math.min(55, Math.max(0, Math.round(normalized / 5) * 5));
};

const getDateTimeKey = (value) => {
  const date = parseDateForStorage(value);
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';

  const roundedMinutes = roundMinutesToNearestFive(date.getMinutes());
  const roundedDate = new Date(date);
  roundedDate.setMinutes(roundedMinutes, 0, 0);

  return [
    roundedDate.getFullYear(),
    String(roundedDate.getMonth() + 1).padStart(2, '0'),
    String(roundedDate.getDate()).padStart(2, '0'),
    String(roundedDate.getHours()).padStart(2, '0'),
    String(roundedMinutes).padStart(2, '0'),
  ].join('-');
};

const formatImportRowPreview = (row = {}) => {
  const note = String(row?.note || row?.text || '').trim();
  if (!note) return '';
  return note.length > 120 ? `${note.slice(0, 117)}…` : note;
};

const formatImportRowTime = (value) => {
  const date = parseDateForStorage(value);
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';

  return date.toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
  });
};

const formatSavedImportRowDate = (value) => {
  const date = parseDateForStorage(value);
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';

  return date.toLocaleDateString([], {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
};

const buildSavedImportRowSummary = ({
  id = '',
  docData = {},
  fallbackFrom = '',
} = {}) => {
  const title = String(
    docData?.title
    || docData?.headline
    || docData?.parsedTitle
    || docData?.text
    || docData?.note
    || ''
  ).trim();
  const preview = formatImportRowPreview({
    note: String(docData?.text || docData?.note || '').trim(),
  });

  return {
    id: String(id || '').trim(),
    category: normalizeImportCategory(docData?.category),
    date: docData?.timestamp || null,
    dateLabel: formatSavedImportRowDate(docData?.timestamp),
    time: formatImportRowTime(docData?.timestamp),
    title: title.length > 120 ? `${title.slice(0, 117)}…` : title,
    preview,
    fallbackFrom: String(fallbackFrom || '').trim() || null,
    yearResolutionStrategy: String(docData?.metadata?.yearResolutionStrategy || '').trim(),
    yearAdjustedFromFuture: Boolean(docData?.metadata?.yearAdjustedFromFuture),
  };
};

const hasAcceptableLocalReviewRows = (rows = []) => (
  Array.isArray(rows)
  && rows.length > 0
  && rows.every((row) => {
    const confidence = String(row?.reviewConfidence || '').trim().toLowerCase();
    return confidence !== 'review' || row?.metadata?.timelineStructuredImport === true;
  })
);

const buildNaturalCaptureFallbackDebug = ({
  localParserUsed = '',
  aiFallbackUsed = false,
  fallbackReason = '',
  enrichmentApplied = false,
} = {}) => ({
  localParserUsed: String(localParserUsed || '').trim(),
  aiFallbackUsed: Boolean(aiFallbackUsed),
  fallbackReason: String(fallbackReason || '').trim(),
  enrichmentApplied: Boolean(enrichmentApplied),
});

const buildTimelineSegmentationImportDebug = (decision = {}, {
  aiTimelineSegmentCount = 0,
} = {}) => ({
  looksLikeCaregiverTimelineImport: Boolean(decision?.looksLikeCaregiverTimelineImport),
  collapsedTimelineParse: Boolean(decision?.collapsedTimelineParse),
  aiTimelineSegmentationTriggered: Boolean(decision?.shouldTrigger),
  localRowCountBeforeAITimelineSplit: Number(decision?.localRowCount || 0),
  anchorCount: Number(decision?.anchorCount || 0),
  monthDayCount: Number(decision?.monthDayCount || 0),
  explicitTimeCount: Number(decision?.explicitTimeCount || 0),
  aiTimelineSegmentCount: Number(aiTimelineSegmentCount || 0),
  timelineSegmentationReason: Array.isArray(decision?.reasons) ? decision.reasons : [],
});

const buildAITimelineSegmentationFailureDebug = (error = null) => ({
  aiTimelineSegmentationFailed: Boolean(error),
  aiTimelineSegmentationError: error
    ? (String(error?.message || error || '').trim() || 'AI timeline segmentation failed')
    : '',
  aiTimelineSegmentationRawPreview: error
    ? String(error?.responseData?.debugRawPreview || error?.debugRawPreview || '').trim()
    : '',
});

const buildAITimelineSegmentationEmptyResultDebug = ({
  attempted = false,
  responseData = null,
  rows = [],
} = {}) => ({
  aiTimelineSegmentationAttempted: Boolean(attempted),
  aiTimelineSegmentationReturnedNoSegments: Boolean(
    attempted
    && Array.isArray(rows)
    && rows.length === 0
  ),
  aiTimelineSegmentationParsedBy: String(
    responseData?.parsedBy || responseData?.source || ''
  ).trim(),
});

const buildImportRowPreviewDebug = (rows = [], {
  keyPrefix = 'localParser',
  maxRows = 6,
} = {}) => {
  const safeRows = Array.isArray(rows) ? rows : [];
  return {
    [`${keyPrefix}RowCount`]: safeRows.length,
    [`${keyPrefix}RowPreview`]: safeRows.slice(0, maxRows).map((row) => {
      const dateValue = row?.date instanceof Date
        ? row.date
        : (row?.date ? new Date(row.date) : null);
      const dateLabel = dateValue instanceof Date && !Number.isNaN(dateValue.getTime())
        ? dateValue.toISOString().slice(0, 10)
        : 'no-date';
      const timeLabel = String(
        row?.localTime
        || row?.time
        || row?.timeLabel
        || row?.metadata?.timeLabel
        || 'no-time'
      ).trim();
      const categoryLabel = String(row?.category || 'uncategorized').trim() || 'uncategorized';
      const textLabel = String(
        row?.title
        || row?.note
        || row?.body
        || row?.text
        || ''
      )
        .trim()
        .replace(/\s+/g, ' ')
        .slice(0, 120);

      return `${dateLabel} | ${timeLabel} | ${categoryLabel} | ${textLabel}`;
    }),
  };
};

const debugAITimelineSegmentationFailure = (label, error, context = {}) => {
  if (!isImportParserDebugEnabled()) return;
  // eslint-disable-next-line no-console
  console.warn(`[ImportParser] ${label}`, {
    error: String(error?.message || error || '').trim() || 'AI timeline segmentation failed',
    ...context,
  });
};

const mergeTimelineSegmentationDecisions = (...decisions) => {
  const normalized = decisions.filter(Boolean);
  return {
    shouldTrigger: normalized.some((decision) => Boolean(decision?.shouldTrigger)),
    looksLikeCaregiverTimelineImport: normalized.some((decision) => Boolean(decision?.looksLikeCaregiverTimelineImport)),
    collapsedTimelineParse: normalized.some((decision) => Boolean(decision?.collapsedTimelineParse)),
    reasons: Array.from(new Set(normalized.flatMap((decision) => Array.isArray(decision?.reasons) ? decision.reasons : []))),
    anchorCount: normalized.reduce((max, decision) => Math.max(max, Number(decision?.anchorCount || 0)), 0),
    inlineStatusDateCount: normalized.reduce((max, decision) => Math.max(max, Number(decision?.inlineStatusDateCount || 0)), 0),
    localRowCount: normalized.reduce((min, decision) => {
      const value = Number(decision?.localRowCount || 0);
      return min === null ? value : Math.min(min, value);
    }, null),
  };
};

const buildSkippedImportRow = ({
  row = {},
  reason = 'firestore_write_error',
  explanation = '',
  severity = 'hard',
  source = 'save',
  error = null,
} = {}) => ({
  preview: formatImportRowPreview(row),
  reason,
  explanation,
  severity,
  source,
  category: normalizeImportCategory(row?.category),
  date: row?.date || null,
  time: formatImportRowTime(row?.date),
  error: error ? {
    code: error?.code || null,
    message: String(error?.message || error || '').trim() || null,
  } : null,
});

const classifyImportSaveError = (error) => {
  const rawMessage = String(error?.message || '').toLowerCase();

  if (
    error?.code === 'permission-denied' ||
    rawMessage.includes('missing or insufficient permissions') ||
    rawMessage.includes('permission-denied')
  ) {
    return {
      reason: 'firestore_write_error',
      explanation: 'We couldn’t save this entry. You can review or edit it and try again.',
      severity: 'hard',
    };
  }

  if (
    error?.code === 'invalid-argument' ||
    rawMessage.includes('invalid') ||
    rawMessage.includes('bad request')
  ) {
    return {
      reason: 'invalid_payload',
      explanation: 'The imported row had invalid data and could not be saved.',
      severity: 'hard',
    };
  }

  return {
    reason: 'firestore_write_error',
    explanation: 'We couldn’t save this entry. You can review or edit it and try again.',
    severity: 'hard',
  };
};

export const fetchExistingLogsForChild = async (childId) => {
  if (!childId) return [];

  const snapshot = await getDocs(
    query(
      collection(db, 'dailyLogs'),
      where('childId', '==', childId),
      where('status', '==', 'active')
    )
  );

  return snapshot.docs.map((snapshotDoc) => {
    const data = snapshotDoc.data();
    const timestamp = data.timestamp?.toDate?.()
      || data.createdAt?.toDate?.()
      || data.timestamp
      || data.createdAt
      || null;

    return {
      id: snapshotDoc.id,
      childId: data.childId || childId,
      category: normalizeImportCategory(data.category),
      importance: data.importance === 'important' ? 'important' : 'normal',
      note: data.text || data.note || data.content || '',
      timestamp,
      dateKey: getDateKey(timestamp),
      normalizedNote: normalizeCategoryText(data.text || data.note || data.content || ''),
      normalizedCategory: normalizeImportCategory(data.category),
    };
  });
};

export const detectPossibleDuplicateImportRow = (row, existingLogs = []) => {
  const rowDateKey = getDateKey(row.date);
  const rowNote = normalizeCategoryText(row.note);
  const rowCategory = normalizeImportCategory(row.category);

  if (!rowNote || !existingLogs.length) {
    return { matched: false, reason: '', existingMatch: null };
  }

  for (const existing of existingLogs) {
    const existingNote = existing.normalizedNote || normalizeCategoryText(existing.note);
    if (!existingNote) continue;

    const categoryMatches = rowCategory === (existing.normalizedCategory || normalizeImportCategory(existing.category));
    const dateMatches = !rowDateKey || !existing.dateKey || rowDateKey === existing.dateKey;
    const exactNoteMatch = rowNote === existingNote;
    const noteContainsMatch = rowNote.length >= 12 && (rowNote.includes(existingNote) || existingNote.includes(rowNote));

    if (categoryMatches && dateMatches && (exactNoteMatch || noteContainsMatch)) {
      return {
        matched: true,
        reason: exactNoteMatch ? 'Possible duplicate' : 'Looks similar to another entry',
        existingId: existing.id,
        existingMatch: {
          id: existing.id,
          category: existing.normalizedCategory || normalizeImportCategory(existing.category),
          date: existing.timestamp || null,
          dateKey: existing.dateKey || '',
          note: existing.note || '',
          preview: formatImportRowPreview(existing),
        },
      };
    }
  }

  return { matched: false, reason: '', existingMatch: null };
};

export const dedupeImportedRowsInBatch = (rows = [], childId = '') => {
  const seen = new Map();
  const dedupedRows = [];
  const duplicates = [];

  rows.forEach((row, index) => {
    const fingerprint = [
      String(childId || '').trim(),
      normalizeImportCategory(row?.category),
      normalizeCategoryText(row?.note),
      getDateKey(row?.date),
      getDateTimeKey(row?.date),
    ].join('|');

    if (!fingerprint.trim()) {
      dedupedRows.push(row);
      return;
    }

    if (seen.has(fingerprint)) {
      duplicates.push({ index, duplicateOfIndex: seen.get(fingerprint), row });
      return;
    }

    seen.set(fingerprint, index);
    dedupedRows.push(row);
  });

  return {
    rows: dedupedRows,
    duplicateCount: duplicates.length,
    duplicates,
  };
};

export const parseImportedFileText = async (text, {
  defaultDate = new Date(),
  childName = null,
} = {}) => {
  // Compatibility wrapper only. New parsing logic should go through parseNaturalCaptureText().
  if (typeof text === 'string' && text.length > MAX_IMPORT_TEXT_LENGTH) {
    throw new Error('That file is too large to import. Please split it into a smaller file and try again.');
  }

  const rawText = String(text || '').trim();
  let aiTimelineSegmentationFailure = null;
  let aiTimelineSegmentationAttemptMeta = null;
  const facadeResult = parseNaturalCaptureText(rawText, {
    defaultDate,
    childName,
    adapterMode: 'imported_file_text',
    parserMode: 'file_import_text',
    sourceMode: 'docx_xlsx',
    inputMode: 'file_import',
    rawText,
  });
  const facadeRows = Array.isArray(facadeResult?.rows) ? facadeResult.rows : [];
  const hasTimelineStructuredLocalRows = facadeRows.some((row) => (
    row?.timelineStructuredImport === true
    || row?.metadata?.timelineStructuredImport === true
  ));
  const looksLikeCaregiverTimelineImport = looksLikeCaregiverTimelineImportText(rawText);

  if (
    hasAcceptableLocalReviewRows(facadeRows)
    || hasTimelineStructuredLocalRows
    || (looksLikeCaregiverTimelineImport && facadeRows.length > 0)
  ) {
    const preDateTimelineSegmentationDecision = shouldUseAITimelineSegmentation({
      rawText,
      rows: facadeRows,
      fallbackDate: defaultDate,
    });
    const datedLocalRows = applyImportDateContextToImportedRows(facadeRows, rawText, {
      fallbackDate: defaultDate,
    });
    const groupedLocalRows = groupRowsForReview(datedLocalRows);
    const postDateTimelineSegmentationDecision = shouldUseAITimelineSegmentation({
      rawText,
      rows: groupedLocalRows,
      fallbackDate: defaultDate,
    });
    const timelineSegmentationDecision = mergeTimelineSegmentationDecisions(
      preDateTimelineSegmentationDecision,
      postDateTimelineSegmentationDecision,
    );

    if (timelineSegmentationDecision.shouldTrigger) {
      try {
        const aiSegmentationResult = await segmentImportedCaregiverTimelineWithAI({
          text: rawText,
          defaultDate,
          childName,
          localRows: groupedLocalRows,
          debugContext: timelineSegmentationDecision,
          callable: invokeParseImportedLogsCallable,
          inputMode: 'file_import',
        });
        aiTimelineSegmentationAttemptMeta = {
          attempted: true,
          responseData: aiSegmentationResult?.responseData || null,
          rows: aiSegmentationResult?.rows || [],
        };

        if (aiSegmentationResult.rows.length > 0) {
          const datedAIRows = applyImportDateContextToImportedRows(aiSegmentationResult.rows, rawText, {
            fallbackDate: defaultDate,
          });
          const groupedAIRows = groupRowsForReview(datedAIRows);
          return {
            entries: groupedAIRows,
            parsedBy: aiSegmentationResult?.responseData?.parsedBy || 'ai_timeline_segmentation',
            segmentedByEventTime: true,
            importDebug: {
              ...(facadeResult?.debug || {}),
              ...buildNaturalCaptureFallbackDebug({
                localParserUsed: facadeResult?.debug?.parserUsed || 'imported_file_text',
                aiFallbackUsed: true,
                fallbackReason: 'caregiver_timeline_segmentation',
                enrichmentApplied: true,
              }),
              ...buildTimelineSegmentationImportDebug(timelineSegmentationDecision, {
                aiTimelineSegmentCount: groupedAIRows.length,
              }),
              ...buildImportRowPreviewDebug(groupedLocalRows, {
                keyPrefix: 'localParser',
              }),
              ...buildImportRowPreviewDebug(groupedAIRows, {
                keyPrefix: 'aiReview',
              }),
            },
          };
        }
      } catch (error) {
        aiTimelineSegmentationFailure = error;
        debugAITimelineSegmentationFailure('file-import-ai-timeline-failed', error, {
          localRowCount: groupedLocalRows.length,
          reasons: timelineSegmentationDecision?.reasons || [],
        });
        // Fall through to existing local behavior for safety.
      }
    }

    return {
      entries: groupedLocalRows,
      parsedBy: facadeResult?.debug?.parserUsed || 'imported_file_text',
      segmentedByEventTime: Boolean(facadeResult?.debug?.underlyingPath === 'local_long_note_parsing'),
      importDebug: {
        ...(facadeResult?.debug || {}),
        ...buildNaturalCaptureFallbackDebug({
          localParserUsed: facadeResult?.debug?.parserUsed || 'imported_file_text',
          aiFallbackUsed: false,
          fallbackReason: 'local_confident',
          enrichmentApplied: false,
        }),
        ...buildTimelineSegmentationImportDebug(timelineSegmentationDecision || {}, {
          aiTimelineSegmentCount: 0,
        }),
        ...buildAITimelineSegmentationFailureDebug(aiTimelineSegmentationFailure),
        ...buildAITimelineSegmentationEmptyResultDebug(aiTimelineSegmentationAttemptMeta || {}),
        ...buildImportRowPreviewDebug(groupedLocalRows, {
          keyPrefix: 'localParser',
        }),
      },
    };
  }

  const parsed = await parseImportedLogs(rawText);
  return {
    ...parsed,
    importDebug: {
      ...(parsed?.importDebug || {}),
      adapterMode: 'imported_file_text',
      parserUsed: facadeResult?.debug?.parserUsed || null,
      underlyingPath: facadeResult?.debug?.underlyingPath || 'fallback_parseImportedLogs',
      reason: facadeResult?.debug?.reason || 'fallback',
      ...buildNaturalCaptureFallbackDebug({
        localParserUsed: facadeResult?.debug?.parserUsed || 'imported_file_text',
        aiFallbackUsed: true,
        fallbackReason: facadeResult?.debug?.reason || 'fallback',
        enrichmentApplied: Boolean(parsed?.entries?.length),
      }),
      ...buildAITimelineSegmentationFailureDebug(aiTimelineSegmentationFailure),
      ...buildAITimelineSegmentationEmptyResultDebug(aiTimelineSegmentationAttemptMeta || {}),
      ...buildImportRowPreviewDebug(parsed?.entries || [], {
        keyPrefix: 'finalReview',
      }),
    },
  };
};

export const parseImportedLogs = async (text) => {
  // Compatibility wrapper only. New parsing logic should go through parseNaturalCaptureText().
  if (typeof text === 'string' && text.length > MAX_IMPORT_TEXT_LENGTH) {
    throw new Error('That file is too large to import. Please split it into a smaller file and try again.');
  }

  const rawText = String(text || '').trim();
  const parsedChildNameFromText = null;
  const parsedDefaultDateFromText = new Date();
  let aiTimelineSegmentationFailure = null;
  let aiTimelineSegmentationAttemptMeta = null;
  const localParserUsedForFallback = isLongNoteEmailText(rawText)
    ? 'natural_capture_long_note_adapter'
    : 'natural_capture_school_adapter';
  traceNaturalCapturePipeline('import-input', buildNaturalCapturePipelineSnapshot({
    stage: 'import-input',
    inputType: detectNaturalCaptureInputType({
      source: 'import',
      isImported: true,
      isLongNote: isLongNoteEmailText(rawText),
    }),
    text: rawText,
  }));
  if (isLongNoteEmailText(rawText)) {
    const facadeResult = parseNaturalCaptureText(rawText, {
      defaultDate: parsedDefaultDateFromText,
      childName: parsedChildNameFromText,
      parserMode: 'long_note_email',
      sourceMode: 'long_note_email',
      inputMode: 'import',
      rawText,
      adapterMode: 'long_note_email',
    });
    const facadeRows = Array.isArray(facadeResult?.rows) ? facadeResult.rows : [];
    const datedLocalRows = applyImportDateContextToImportedRows(facadeRows, rawText, {
      fallbackDate: parsedDefaultDateFromText,
    });
    const groupedLocalRows = groupRowsForReview(datedLocalRows);
    traceNaturalCapturePipeline('import-long-note-review', buildNaturalCapturePipelineSnapshot({
      stage: 'import-long-note-review',
      inputType: detectNaturalCaptureInputType({
        source: 'import',
        isImported: true,
        isLongNote: true,
      }),
      text: rawText,
      rows: datedLocalRows,
      groupedRows: groupedLocalRows,
      reviewRows: groupedLocalRows,
    }));

    if (groupedLocalRows.length > 0) {
      if (isImportParserDebugEnabled()) {
        // eslint-disable-next-line no-console
        console.warn('[ImportParser] import-long-note-email', {
          parsedBy: 'long_note_email',
          localParsedRowCount: facadeRows.length,
          groupedRowCount: groupedLocalRows.length,
          textLength: rawText.length,
          preview: rawText.slice(0, 180),
        });
      }
      return {
        entries: groupedLocalRows,
        parsedBy: 'long_note_email',
        segmentedByEventTime: true,
        importDebug: {
          ...buildNaturalCaptureFallbackDebug({
            localParserUsed: localParserUsedForFallback,
            aiFallbackUsed: false,
            fallbackReason: 'local_confident',
            enrichmentApplied: false,
          }),
          ...buildImportRowPreviewDebug(facadeRows, {
            keyPrefix: 'localParser',
          }),
          ...buildImportRowPreviewDebug(groupedLocalRows, {
            keyPrefix: 'finalReview',
          }),
        },
      };
    }
  }

  const localFacade = parseNaturalCaptureText(rawText, {
    defaultDate: parsedDefaultDateFromText,
    childName: parsedChildNameFromText,
    adapterMode: 'school_pasted_text',
    parserMode: 'school_text',
    sourceMode: 'school_text',
    inputMode: 'school_pasted_text',
    rawText,
  });
  const localFacadeRows = Array.isArray(localFacade?.rows) ? localFacade.rows : [];
  const preDateTimelineSegmentationDecision = shouldUseAITimelineSegmentation({
    rawText,
    rows: localFacadeRows,
    fallbackDate: parsedDefaultDateFromText,
  });
  const datedLocalRows = applyImportDateContextToImportedRows(
    localFacadeRows,
    rawText,
    {
      fallbackDate: parsedDefaultDateFromText,
    }
  );
  const groupedLocalRows = groupRowsForReview(datedLocalRows);
  const looksLikeCaregiverTimelineImport = looksLikeCaregiverTimelineImportText(rawText);
  const postDateTimelineSegmentationDecision = shouldUseAITimelineSegmentation({
    rawText,
    rows: groupedLocalRows,
    fallbackDate: parsedDefaultDateFromText,
  });
  const timelineSegmentationDecision = mergeTimelineSegmentationDecisions(
    preDateTimelineSegmentationDecision,
    postDateTimelineSegmentationDecision,
  );
  const localRowsAcceptable = hasAcceptableLocalReviewRows(groupedLocalRows)
    || groupedLocalRows.some((row) => (
      row?.timelineStructuredImport === true
      || row?.metadata?.timelineStructuredImport === true
    ))
    || (looksLikeCaregiverTimelineImport && groupedLocalRows.length > 0);

  if (timelineSegmentationDecision.shouldTrigger && groupedLocalRows.length > 0) {
    try {
      const aiSegmentationResult = await segmentImportedCaregiverTimelineWithAI({
        text: rawText,
        defaultDate: parsedDefaultDateFromText,
        childName: parsedChildNameFromText,
        localRows: groupedLocalRows,
        debugContext: timelineSegmentationDecision,
        callable: invokeParseImportedLogsCallable,
      });
      aiTimelineSegmentationAttemptMeta = {
        attempted: true,
        responseData: aiSegmentationResult?.responseData || null,
        rows: aiSegmentationResult?.rows || [],
      };

      if (aiSegmentationResult.rows.length > 0) {
        const datedAIRows = applyImportDateContextToImportedRows(aiSegmentationResult.rows, rawText, {
          fallbackDate: parsedDefaultDateFromText,
        });
        const groupedAIRows = groupRowsForReview(datedAIRows);

        return {
          entries: groupedAIRows,
          parsedBy: aiSegmentationResult?.responseData?.parsedBy || 'ai_timeline_segmentation',
          importDebug: {
            ...buildNaturalCaptureFallbackDebug({
              localParserUsed: localParserUsedForFallback,
              aiFallbackUsed: true,
              fallbackReason: 'caregiver_timeline_segmentation',
              enrichmentApplied: groupedAIRows.length > 0,
            }),
            ...buildTimelineSegmentationImportDebug(timelineSegmentationDecision, {
              aiTimelineSegmentCount: groupedAIRows.length,
            }),
            ...buildImportRowPreviewDebug(groupedLocalRows, {
              keyPrefix: 'localParser',
            }),
            ...buildImportRowPreviewDebug(groupedAIRows, {
              keyPrefix: 'aiReview',
            }),
          },
        };
      }
    } catch (error) {
      aiTimelineSegmentationFailure = error;
      debugAITimelineSegmentationFailure('import-ai-timeline-failed', error, {
        localRowCount: groupedLocalRows.length,
        reasons: timelineSegmentationDecision?.reasons || [],
      });
      // Fall through to the current local/backend behavior.
    }
  }

  if (localRowsAcceptable) {
    traceNaturalCapturePipeline('import-local-school-review', buildNaturalCapturePipelineSnapshot({
      stage: 'import-local-school-review',
      inputType: detectNaturalCaptureInputType({
        source: 'import',
        isImported: true,
        isLongNote: false,
      }),
      text: rawText,
      rows: datedLocalRows,
      groupedRows: groupedLocalRows,
      reviewRows: groupedLocalRows,
      metadata: {
        localParserUsed: 'natural_capture_school_adapter',
      },
    }));

    return {
      entries: groupedLocalRows,
      parsedBy: 'school_text',
      importDebug: {
        ...buildNaturalCaptureFallbackDebug({
          localParserUsed: 'natural_capture_school_adapter',
          aiFallbackUsed: false,
          fallbackReason: 'local_confident',
          enrichmentApplied: false,
        }),
        ...buildTimelineSegmentationImportDebug(timelineSegmentationDecision, {
          aiTimelineSegmentCount: 0,
        }),
        ...buildAITimelineSegmentationFailureDebug(aiTimelineSegmentationFailure),
        ...buildAITimelineSegmentationEmptyResultDebug(aiTimelineSegmentationAttemptMeta || {}),
        ...buildImportRowPreviewDebug(groupedLocalRows, {
          keyPrefix: 'localParser',
        }),
      },
    };
  }

  const response = await invokeParseImportedLogsCallable({ text });
  const responseData = response.data || {};
  const parsedEntries = normalizeBackendCaptureReviewRows(
    Array.isArray(responseData?.entries) ? responseData.entries : [],
    {
      parserMode: 'backend_import',
      sourceMode: 'import_backend',
      inputMode: 'import',
      rawText,
      backendSource: responseData?.source || responseData?.parsedBy || 'callable',
      parsedBy: responseData?.parsedBy || responseData?.source || 'callable',
      reviewNeeded: responseData?.reviewNeeded,
    }
  );
  const parsedDefaultDate = parsedEntries.find((entry) => entry?.date)?.date || new Date();
  const importedDateContexts = extractImportDateContexts(rawText, {
    fallbackDate: parsedDefaultDate,
  }).filter((context) => context?.date);
  const datedParsedEntries = applyImportDateContextToImportedRows(parsedEntries, rawText, {
    fallbackDate: parsedDefaultDate,
  });
  const groupedParsedEntries = groupRowsForReview(datedParsedEntries);
  traceNaturalCapturePipeline('import-callable-parser', buildNaturalCapturePipelineSnapshot({
    stage: 'import-callable-parser',
    inputType: detectNaturalCaptureInputType({
      source: 'import',
      isImported: true,
      isLongNote: false,
    }),
    text: rawText,
    rows: datedParsedEntries,
    groupedRows: groupedParsedEntries,
    reviewRows: groupedParsedEntries,
    metadata: {
      parsedBy: responseData?.parsedBy || responseData?.source || 'callable',
    },
  }));

  const parsedEntriesContainMultiTimeNotes = datedParsedEntries.some((entry) => {
    const noteText = String(entry?.note || entry?.text || '').trim();
    return countCareExplicitTimes(noteText) > 1;
  });

  if (isImportParserDebugEnabled()) {
    // eslint-disable-next-line no-console
    console.warn('[ImportParser] import-callable-result', {
      parsedBy: responseData?.parsedBy || responseData?.source || 'callable',
      parsedEntryCount: datedParsedEntries.length,
      groupedParsedEntryCount: groupedParsedEntries.length,
      selectedRowCount: groupedParsedEntries.length,
      textLength: rawText.length,
      preview: rawText.slice(0, 180),
      localParserUsed: 'natural_capture_school_adapter',
      aiFallbackUsed: true,
      fallbackReason: importedDateContexts.length ? 'date_context_ambiguous' : (parsedEntriesContainMultiTimeNotes ? 'multi_time_note' : 'local_review_needed'),
    });
  }

  return {
    ...responseData,
    entries: groupedParsedEntries,
    importDebug: {
      ...buildNaturalCaptureFallbackDebug({
        localParserUsed: localParserUsedForFallback,
        aiFallbackUsed: true,
        fallbackReason: importedDateContexts.length ? 'date_context_ambiguous' : (parsedEntriesContainMultiTimeNotes ? 'multi_time_note' : 'local_review_needed'),
        enrichmentApplied: groupedParsedEntries.length > 0,
      }),
      ...buildTimelineSegmentationImportDebug(timelineSegmentationDecision, {
        aiTimelineSegmentCount: 0,
      }),
      ...buildAITimelineSegmentationFailureDebug(aiTimelineSegmentationFailure),
      ...buildAITimelineSegmentationEmptyResultDebug(aiTimelineSegmentationAttemptMeta || {}),
      ...buildImportRowPreviewDebug(groupedParsedEntries, {
        keyPrefix: 'finalReview',
      }),
    },
  };
};

export const saveImportedLogs = async ({
  rows = [],
  childId,
  user,
  source = 'import',
  mode = 'import',
}) => {
  if (!childId) throw new Error('Please select a child before importing logs.');
  if (!user?.uid) throw new Error('You must be signed in to import logs.');
  if (!rows.length) throw new Error('There are no rows to save.');

  const resolvedSource = String(source || 'import').trim() || 'import';
  const resolvedMode = String(mode || 'import').trim() || 'import';
  const captureContext = await createCapture({
    childId,
    userId: user.uid,
    sourceType: resolvedSource,
    sourceText: buildImportCaptureSourceText(rows),
    originalText: buildImportCaptureSourceText(rows),
    inputContext: {
      entryPoint: 'import_logs',
      mode: resolvedMode,
      rowCount: rows.length,
    },
    metadata: {
      source: resolvedSource,
      mode: resolvedMode,
    },
    status: CAPTURE_PROCESSING_STATUSES.SAVED,
  });

  const normalizedRows = rows.map((row) => {
    const normalizedDate = parseDateForStorage(row?.date || row?.timestamp || new Date());
    const safeDate = Number.isNaN(normalizedDate.getTime()) ? new Date() : normalizedDate;
    const safeDateKey = formatLocalDateForStorage(safeDate);

    const importDocData = buildPreservedImportDocData(row, {
      childId,
      createdBy: user?.uid,
      createdAt: serverTimestamp(),
      source: {
        type: resolvedSource,
        mode: resolvedMode,
      },
      mode: resolvedMode,
      status: row?.status || 'active',
      entryDate: safeDate.toDateString(),
      entryDayKey: row?.entryDayKey || row?.localDate || safeDateKey || null,
      localDate: row?.localDate || row?.entryDayKey || safeDateKey || null,
      localTime: row?.timeSource === 'explicit' ? formatLocalTimeForStorage(safeDate) : row?.localTime || null,
      timestamp: safeDate,
      authorId: user?.uid,
      authorName: user?.displayName || user?.email?.split('@')[0] || 'User',
      authorEmail: user?.email,
      tags: [],
      metadata: row?.metadata || null,
    });
    const docDataWithCapture = {
      ...importDocData,
      ...buildCaptureTimelineFields({
        ...(captureContext || {}),
        sourceBlockId: row?.sourceBlockId || row?.metadata?.sourceBlockId || null,
        sourceSentenceIndex: Number.isInteger(row?.sourceSentenceIndex)
          ? row.sourceSentenceIndex
          : Number.isInteger(row?.metadata?.sourceSentenceIndex)
            ? row.metadata.sourceSentenceIndex
            : null,
      }),
    };
    return {
      ...docDataWithCapture,
      artifactType: getReviewedRowArtifactType(row, docDataWithCapture),
    };
  });
  const skippedTimelineRows = [];
  const saveRows = normalizedRows.map((docData, index) => {
    const row = rows[index] || {};
    const collectionName = isKnowledgeItemArtifact(docData.artifactType)
      ? KNOWLEDGE_ITEMS_COLLECTION
      : 'dailyLogs';
    if (
      collectionName === 'dailyLogs'
      && shouldSkipTimelineSave({
        ...row,
        ...docData,
        record: row.record || docData.record || docData,
        metadata: {
          ...(row.metadata || {}),
          ...(docData.metadata || {}),
        },
      })
    ) {
      skippedTimelineRows.push({
        index,
        ...buildSkippedImportRow({
          row: docData,
          reason: 'not_timeline_event',
          explanation: 'This looks like care knowledge or a reference source, not a timeline event.',
          severity: 'soft',
          source: 'timeline_guard',
        }),
      });
      return null;
    }
    const saveData = collectionName === KNOWLEDGE_ITEMS_COLLECTION
      ? buildKnowledgeItemFromReviewedRow({ row, docData, user })
      : docData;
    return {
      collectionName,
      docData,
      saveData,
    };
  }).filter(Boolean);

  traceNaturalCapturePipeline('save-payload', buildNaturalCapturePipelineSnapshot({
    stage: 'save-payload',
    inputType: detectNaturalCaptureInputType({
      source: resolvedSource,
      isImported: resolvedSource === 'import',
      route: resolvedSource === 'quick_log_smart_capture' || resolvedMode === 'smart_capture'
        ? 'claude'
        : 'rule_only',
    }),
    rows: normalizedRows,
    savedRows: saveRows.map(({ docData }) => docData),
    metadata: {
      childId,
      source: resolvedSource,
      mode: resolvedMode,
    },
  }));

  const batch = writeBatch(db);
  saveRows.forEach(({ collectionName, docData, saveData }) => {
    const docRef = doc(collection(db, collectionName));
    batch.set(docRef, saveData);
  });

  try {
    const savedRows = [];
    await batch.commit();
    saveRows.forEach(({ collectionName, docData, saveData }, index) => {
      savedRows.push(buildSavedImportRowSummary({
        id: `batch:${index}`,
        docData: collectionName === KNOWLEDGE_ITEMS_COLLECTION ? saveData : docData,
      }));
    });
    return {
      count: saveRows.length,
      mode: 'batch',
      skippedCount: skippedTimelineRows.length,
      skippedRows: skippedTimelineRows,
      hardSkippedCount: 0,
      savedRows,
    };
  } catch (error) {
    const savedRows = [];
    const skippedRows = [...skippedTimelineRows];

    for (const [index, saveRow] of saveRows.entries()) {
      const { collectionName, docData, saveData } = saveRow;
      try {
        const docRef = await addDoc(collection(db, collectionName), saveData);
        savedRows.push(buildSavedImportRowSummary({
          index,
          id: docRef.id,
          docData: collectionName === KNOWLEDGE_ITEMS_COLLECTION ? saveData : docData,
        }));
      } catch (rowError) {
        let finalRowError = rowError;
        if (
          collectionName !== KNOWLEDGE_ITEMS_COLLECTION
          && normalizeImportCategory(docData?.category) === 'behavior'
          && !docData?.parsedObservation
        ) {
          try {
            const fallbackDocData = buildBehaviorNoteFallbackDocData(docData);
            const fallbackDocRef = await addDoc(collection(db, 'dailyLogs'), fallbackDocData);
            savedRows.push(buildSavedImportRowSummary({
              index,
              id: fallbackDocRef.id,
              docData: fallbackDocData,
              fallbackFrom: 'behavior-note',
            }));
            continue;
          } catch (behaviorFallbackError) {
            finalRowError = behaviorFallbackError;
            try {
              const observationFallbackDocData = buildBehaviorObservationFallbackDocData(docData);
              const observationFallbackDocRef = await addDoc(collection(db, 'dailyLogs'), observationFallbackDocData);
              savedRows.push(buildSavedImportRowSummary({
                index,
                id: observationFallbackDocRef.id,
                docData: observationFallbackDocData,
                fallbackFrom: 'behavior-observation',
              }));
              continue;
            } catch (behaviorObservationFallbackError) {
              finalRowError = behaviorObservationFallbackError;
            }
          }
        }

        const classification = classifyImportSaveError(finalRowError);
        skippedRows.push({
          index,
          ...buildSkippedImportRow({
            row: docData,
            reason: classification.reason,
            explanation: classification.explanation,
            severity: classification.severity,
            source: 'save',
            error: finalRowError,
          }),
        });
      }
    }

    return {
      count: savedRows.length,
      mode: 'row-fallback',
      skippedCount: skippedRows.length,
      skippedRows,
      hardSkippedCount: skippedRows.filter((row) => row.severity === 'hard').length,
      savedRows,
    };
  }
};

export { resolveImportPreviewTiming } from './importLogsParsing';
export {
  buildBehaviorImportDocData,
  buildPreservedImportDocData,
  buildParsedObservationImportDocData,
};

export const IMPORT_LOG_CATEGORIES = [
  'behavior',
  'health',
  'milestone',
  'note',
  'daily',
  'other',
];
